/**
 * DebtFocusTable — v3.7.5 Phase 84.29
 *
 * Ranks the user's active lump-sum liabilities by interest rate
 * (descending) so it's obvious which loan is the fastest win to
 * accelerate. Companion to the hero "highest-interest debt" nudge
 * on the Overview Dashboard.
 *
 * Columns:
 *   • Name (+ kind chip)
 *   • Interest rate (%)
 *   • Outstanding balance
 *   • Monthly EMI
 *   • Months to zero (loan-payoff formula) — or an "Increase EMI to
 *     pay off" hint when the EMI is at or below monthly interest and
 *     the debt never amortises.
 *
 * Credit cards are excluded (their revolving nature makes the ranking
 * misleading — the dashboard has a dedicated recurring-expense card).
 *
 * Props:
 *   items        [{ id, name, kind, interest_rate, outstanding, emi_amount, currency }]
 *   currency     Base currency for display when a liability lacks its own.
 *   maxRows      Optional cap — pass `3` for the compact dashboard preview.
 *   compact      When true, hides the intro copy and shrinks paddings.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatMoney } from '@/lib/format';
import { TrendingUp, AlertCircle } from 'lucide-react';

// Kinds that behave as amortising lump-sum loans and are meaningful to
// rank. Credit cards + accounts (revolving) are excluded upstream.
const RANKED_KINDS = new Set([
  // Raw kinds from the `liabilities` and `wealth_holdings.deposit` tables.
  'home_loan', 'car_loan', 'auto_loan', 'personal_loan',
  'education_loan', 'student_loan', 'bnpl', 'loan_on_card',
  'other_loan', 'other_debt',
  // Normalised UI types emitted by useAccountsData when a caller passes
  // the {category:'liabilities'} shape — so /accounts?tab=liabilities can
  // feed the table without re-mapping raw kinds.
  'loan', 'mortgage',
]);

// APR unit adapter. Some rows in the wild store the interest rate as a
// decimal fraction (0.085 = 8.5%) while others use the percent form
// (8.5). Heuristic: any positive rate strictly less than 1.0 is treated
// as a fraction and scaled up by 100. Zero is left as zero so the
// "no rate" branch still fires. Users are unlikely to have a legitimate
// APR under 1% and the false-positive cost (a 0.4% loan showing 40%)
// is far cheaper than the alternative (an 8.6% loan showing 0.1%,
// which was surfaced in iteration_115 findings for demo persona).
function normalizeAprPct(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n < 1 ? n * 100 : n;
}

// Standard amortising-loan payoff formula.
// n = -log(1 - r*P/M) / log(1 + r)   where r = monthly rate, P = principal,
// M = monthly payment. Returns:
//   { months: <number, or null>, blocked: true|false }
// blocked=true means the EMI is at or below the monthly interest, so the
// debt never amortises.
export function monthsToZero(principal, annualRatePct, monthlyPayment) {
  const P = Number(principal) || 0;
  const M = Number(monthlyPayment) || 0;
  const annualR = normalizeAprPct(annualRatePct);
  if (P <= 0) return { months: 0, blocked: false };
  if (M <= 0) return { months: null, blocked: true };
  const r = annualR / 100 / 12;
  if (r <= 0) {
    // Zero-interest loans amortise linearly.
    return { months: Math.ceil(P / M), blocked: false };
  }
  const monthlyInterest = P * r;
  if (M <= monthlyInterest) {
    return { months: null, blocked: true };
  }
  const n = -Math.log(1 - (r * P) / M) / Math.log(1 + r);
  if (!Number.isFinite(n) || n <= 0) return { months: null, blocked: true };
  return { months: Math.ceil(n), blocked: false };
}

function humanMonths(m) {
  if (m == null) return '—';
  if (m < 12) return `${m} mo`;
  const years = Math.floor(m / 12);
  const rem = m % 12;
  return rem === 0 ? `${years}y` : `${years}y ${rem}mo`;
}

export default function DebtFocusTable({ items = [], currency = 'INR', maxRows, compact = false }) {
  const ranked = useMemo(() => {
    const rows = (items || [])
      .filter((i) => {
        const kind = String(i?.kind || '').toLowerCase();
        if (!RANKED_KINDS.has(kind)) return false;
        const out = Number(i?.outstanding || 0);
        return out > 0;
      })
      .map((i) => {
        const rate = normalizeAprPct(i?.interest_rate);
        const payoff = monthsToZero(i.outstanding, i.interest_rate, i.emi_amount);
        return {
          id: i.id,
          name: i.name || 'Untitled debt',
          kind: String(i.kind || 'other_debt'),
          rate,
          outstanding: Number(i.outstanding || 0),
          emi: Number(i.emi_amount || 0),
          currency: i.currency || currency,
          months: payoff.months,
          blocked: payoff.blocked,
        };
      })
      .sort((a, b) => b.rate - a.rate);
    return typeof maxRows === 'number' ? rows.slice(0, maxRows) : rows;
  }, [items, currency, maxRows]);

  if (!ranked.length) {
    return null;
  }

  return (
    <Card
      className="rounded-2xl border-border/70"
      data-testid="debt-focus-table"
    >
      <CardContent className={compact ? 'p-4' : 'p-5'}>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-destructive" />
          <div className="font-display font-semibold text-base">
            Debt focus
          </div>
          <Badge variant="outline" className="rounded-full text-[10px] ml-auto">
            Highest interest first
          </Badge>
        </div>
        {!compact && (
          <p className="text-xs text-muted-foreground mb-3">
            Rupees you throw at the top loan save more than at any other. Sorted by APR.
          </p>
        )}
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm tabular-nums"
            data-testid="debt-focus-table-inner"
          >
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 pr-3 font-medium">Loan</th>
                <th className="text-right py-2 px-3 font-medium">APR</th>
                <th className="text-right py-2 px-3 font-medium">Outstanding</th>
                <th className="text-right py-2 px-3 font-medium">EMI</th>
                <th className="text-right py-2 pl-3 font-medium">To zero</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border/60"
                  data-testid={`debt-focus-row-${r.id?.toString().slice(0, 8)}`}
                >
                  <td className="py-2.5 pr-3">
                    <div className="text-sm font-medium truncate max-w-[220px]">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
                      {r.kind.replace(/_/g, ' ')}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right font-medium">
                    {r.rate > 0 ? `${r.rate.toFixed(1)}%` : '—'}
                  </td>
                  <td className="py-2.5 px-3 text-right text-destructive">
                    −{formatMoney(r.outstanding, r.currency, { compact: true })}
                  </td>
                  <td className="py-2.5 px-3 text-right text-muted-foreground">
                    {r.emi > 0 ? formatMoney(r.emi, r.currency, { compact: true }) : '—'}
                  </td>
                  <td
                    className="py-2.5 pl-3 text-right"
                    data-testid={`debt-focus-months-${r.id?.toString().slice(0, 8)}`}
                  >
                    {r.blocked ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                        <AlertCircle className="h-3 w-3" />
                        Increase EMI to pay off
                      </span>
                    ) : (
                      <span className="font-medium">{humanMonths(r.months)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {compact && (
          <div className="mt-3 text-right">
            <Link
              to="/liabilities"
              className="text-xs text-primary hover:underline"
              data-testid="debt-focus-see-all"
            >
              See full debt focus →
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
